/**
 * algolia-quick-add.js
 *
 * Quick-add-to-cart web component for Algolia InstantSearch product cards.
 *
 * Supports both single-variant and multi-variant products:
 *   - Single available variant  → click "Quick Add · [Size]" → add to cart immediately
 *   - Multiple available variants → click "Quick Add" → size picker expands → pick size → add
 *
 * Variant data is fetched from /products/{handle}.js (the .json endpoint omits
 * the `available` field) and cached in a module-level Map.
 *
 * Theme settings are read from the <algolia-quick-add> element's data attributes,
 * set by the Liquid snippet's {% schema %} block.
 *
 * Wrapped in an IIFE to avoid global lexical scope collisions with other
 * deferred scripts in the Shopify theme.
 */
(function () {
  'use strict';

  /* ------------------------------------------------------------------ */
  /*  Caches                                                             */
  /* ------------------------------------------------------------------ */
  const _productCache = new Map();   // handle → Promise<{variants: [{id, option1, available}]}>
  const _troveSectionCache = new Map(); // handle → Promise<Map<handle, variant[]>>
  const _addedVariants = new Set();  // variant IDs already added this session
  
  function sortVariants(variants = []) {
    return variants.sort((a, b) => {
      // size.rank is an opaque, zero-padded sortable string key (e.g.
      // "0026007Q006mzzzz"), not a number - compare it lexicographically.
      // condition.rank is a plain small integer — compare it numerically.
      const aSizeRank = a.variant_data.size.rank;
      const bSizeRank = b.variant_data.size.rank;

      if (aSizeRank === bSizeRank) {
        const aConditionRank = Number(a.variant_data.condition.rank);
        const bConditionRank = Number(b.variant_data.condition.rank);
        return aConditionRank - bConditionRank;
      }
      return aSizeRank < bSizeRank ? -1 : 1;
    });
  }

  function groupBySize(variants) {
    const groups = [];
    const bySizeKey = new Map();
    variants.forEach(v => {
      const key = v.sizeKey || v.option1;
      let group = bySizeKey.get(key);
      if (!group) {
        group = { key, display: v.option1, variants: [] };
        bySizeKey.set(key, group);
        groups.push(group);
      }
      group.variants.push(v);
    });
    return groups;
  }

  // Hardcoded size order, used ONLY when the /products/{handle}.js fallback is
  // hit (no rank info available). When variants come from the trove-variants
  // available_ref metaobject, sortVariants() above is used instead.
  const HARDCODED_SIZE_ORDER = [
    'ALL', 'SHORT', 'REGULAR', 'TALL', 'O/S',
    'PP', 'PS', 'PM', 'PL',
    'XXS', '2XS', 'XSS', 'XS', 'XS/S',
    'S', 'SML', 'SMALL', 'S/M',
    'M', 'MED', 'MEDIUM', 'MT', 'M/L',
    'L', 'LRG', 'LARGE', 'L/XL',
    'XL', 'XLG', 'X LARGE', 'EXTRA LARGE',
    '1X', 'XXL', 'XXLRG', '2X', '2XL', '2XLRG',
    '3X', '3XLG', 'XXXL', '3XL',
    'XXXXL', '4XL', '4XLRG',
    '5XL', '5XLG',
  ];

  function fetchProduct(handle) {
    if (_productCache.has(handle)) return _productCache.get(handle);
    const promise = fetch(`${window.Shopify.routes.root}products/${handle}.js`)
      .then(r => r.json())
      .then(product => {
        const variants = product.variants.map(v => ({
          id: v.id,
          option1: v.option1 || '',
          option2: v.option2 || '',
          available: v.available,
          title: v.title,
        }));
        return { variants };
      })
      .catch(err => {
        console.error(`[quick-add] Failed to fetch product for ${handle}`, err);
        _productCache.delete(handle);
        return null;
      });
    _productCache.set(handle, promise);
    return promise;
  }

  // Groups the trove metaobject's availableVariants list (every sibling color)
  // by handle, mapping each entry into the {id, option1, option2, sizeKey, ...}
  // shape the rest of this file already expects.
  function buildHandleMap(availableVariants) {
    const sorted = sortVariants(availableVariants.slice());
    const byHandle = new Map();
    sorted.forEach(v => {
      if (!byHandle.has(v.handle)) byHandle.set(v.handle, []);
      byHandle.get(v.handle).push({
        id: parseInt(v.variantId.split('/').pop(), 10),
        option1: v.variant_data?.size?.display ?? '',
        option2: v.variant_data?.condition?.display ?? '',
        sizeKey: v.variant_data?.size?.code ?? v.variant_data?.size?.display ?? '',
        available: true,
        title: v.variant_data?.size?.display ?? '',
      });
    });
    return byHandle;
  }

  // Fetches the live trove-variants metaobject/metafield for a product via
  // Shopify's section-rendering API (see sections/trove-variant-data.liquid),
  // used when the Algolia-indexed hit only carries an available_ref pointer
  // rather than the full variant data. Only the metaobject's available_0 field
  // is ever populated today.
  function fetchTroveVariantData(handle) {
    if (_troveSectionCache.has(handle)) return _troveSectionCache.get(handle);
    const promise = fetch(`${window.Shopify.routes.root}products/${handle}?section_id=trove-variant-data`)
      .then(r => r.text())
      .then(html => {
        const tmp = document.createElement('div');
        tmp.innerHTML = html;
        const script = tmp.querySelector('#trove-variant-data');
        if (!script) return new Map();
        const { metaobject, fallback } = JSON.parse(script.textContent);
        const availableVariants = metaobject?.available_0?.availableVariants
          ?? fallback?.availableVariants
          ?? [];
        return buildHandleMap(availableVariants);
      })
      .catch(err => {
        console.error(`[quick-add] Failed to fetch trove variant data for ${handle}`, err);
        _troveSectionCache.delete(handle);
        return new Map();
      });
    _troveSectionCache.set(handle, promise);
    return promise;
  }


  /* ------------------------------------------------------------------ */
  /*  Cart helpers                                                       */
  /* ------------------------------------------------------------------ */

  function openCartDrawer() {
    const cartDrawer = document.querySelector('cart-drawer');
    if (!cartDrawer || typeof cartDrawer.open !== 'function') return;
    fetch(`${window.Shopify.routes.root}?sections=cart-drawer`)
      .then(r => r.json())
      .then(sections => {
        const drawerEl = document.getElementById('CartDrawer');
        if (drawerEl && sections['cart-drawer']) {
          const tmp = document.createElement('div');
          tmp.innerHTML = sections['cart-drawer'];
          const newInner = tmp.querySelector('.drawer__inner');
          const curInner = drawerEl.querySelector('.drawer__inner');
          if (newInner && curInner) curInner.innerHTML = newInner.innerHTML;
        }
        cartDrawer.open();
      })
      .catch(() => cartDrawer.open());
  }


  /* ------------------------------------------------------------------ */
  /*  Component                                                          */
  /* ------------------------------------------------------------------ */
  class AlgoliaQuickAdd extends HTMLElement {
    constructor() {
      super();
      this.render = this.render.bind(this);
    }

    /* ---- Read settings from data attributes (set by Liquid schema) -- */
    get settings() {
      if (this._settings) return this._settings;
      this._settings = {
        buttonText:     this.dataset.buttonText || 'Quick Add',
        successText:    this.dataset.successText || 'Added \u2713',
        soldOutText:    this.dataset.soldOutText || 'Sold Out',
        inBagText:      this.dataset.inBagText || 'Already in Bag',
        unavailableText:this.dataset.unavailableText || 'Unavailable',
        conditionStepEnabled: this.dataset.conditionStepEnabled === 'true',
        backStyle:      this.dataset.backStyle || 'text',
        backText:       this.dataset.backText || 'Back',
        maxSizeOptions: parseInt(this.dataset.maxSizeOptions, 10) || 0,
        moreOptionsText: this.dataset.moreOptionsText || '+{count} More',
      };
      return this._settings;
    }

    /* ---- public API called by algolia-hit.js ------------------------ */
    render(item) {
      const s = this.settings;
      const wrapper = document.createElement('div');
      wrapper.classList.add('algolia-quick-add');

      wrapper._item = item;
      wrapper._handle = item.handle;
      wrapper._busy = false;
      wrapper._backIconTemplate = this.querySelector('template[data-back-icon-template]');

      const size = item.option1 || '';
      const currentSizes = item.meta?.['trove-facets']?.size;
      const isMultiVariant = currentSizes && currentSizes.length > 1;

      // --- Trigger bar ---
      const triggerBar = document.createElement('button');
      triggerBar.classList.add('algolia-quick-add__trigger');
      triggerBar.setAttribute('type', 'button');
      triggerBar.setAttribute('aria-label', `Quick add ${item.title}`);
      triggerBar.textContent = (!isMultiVariant && size) ? `${s.buttonText} \u00B7 ${size}` : s.buttonText;
      triggerBar.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this._handleTriggerClick(wrapper, triggerBar);
      });

      // --- Size picker panel (hidden initially) ---
      const panel = document.createElement('div');
      panel.classList.add('algolia-quick-add__panel');
      panel.style.display = 'none';

      wrapper.appendChild(triggerBar);
      wrapper.appendChild(panel);

      // Listen for color selection from the carousel
      wrapper.addEventListener('colorchange', (e) => {
        const { handle, sizes } = e.detail;
        wrapper._handle = handle;
        wrapper._busy = false;
        panel.style.display = 'none';
        panel.innerHTML = '';
        wrapper.classList.remove('algolia-quick-add--open');
        triggerBar.disabled = false;
        triggerBar.className = 'algolia-quick-add__trigger';
        triggerBar.style.display = '';
        triggerBar.textContent = sizes && sizes.length === 1
          ? `${s.buttonText} \u00B7 ${sizes[0]}`
          : s.buttonText;
      });

      return wrapper;
    }

    /* ---- Trigger click: decide single-add vs size picker ------------ */
    async _handleTriggerClick(wrapper, btn) {
      if (wrapper._busy) return;
      wrapper._busy = true;
      wrapper.classList.add('algolia-quick-add--open');

      const origText = btn.textContent;
      btn.textContent = 'Loading\u2026';
      btn.disabled = true;

      try {
        let available;
        const availableRef = wrapper._item?.meta?.['trove-variants']?.available_ref;
        // The condition step runs off whatever data is available: a live
        // available_ref fetch when the hit carries a pointer to the trove
        // metaobject (size/condition rank included), or option1/option2 +
        // the hardcoded size order from the /products/{handle}.js fallback
        // when it doesn't.
        const useConditionStep = this.settings.conditionStepEnabled;

        if (availableRef) {
          const handleMap = await fetchTroveVariantData(wrapper._handle);
          available = handleMap.get(wrapper._handle);
        }

        // available_ref missing, or its fetch had nothing for this handle —
        // fall back to the live Shopify product fetch.
        if (!available || available.length === 0) {
          const product = await fetchProduct(wrapper._handle);
          if (!product) {
            this._showTempState(wrapper, btn, origText, this.settings.unavailableText, '--soldout');
            return;
          }
          available = product.variants.filter(v => v.available);
          available.sort((a, b) => {
            let indexA = HARDCODED_SIZE_ORDER.indexOf(a.option1);
            let indexB = HARDCODED_SIZE_ORDER.indexOf(b.option1);
            if (indexA === -1) indexA = 999;
            if (indexB === -1) indexB = 999;
            return indexA - indexB;
          });
        }

        if (available.length === 0) {
          this._showTempState(wrapper, btn, origText, this.settings.soldOutText, '--soldout');
          return;
        }

        if (useConditionStep) {
          // Condition is always a required click, even with only one option.
          // Size only skips its own step when there's just one — same
          // shortcut the trigger label already implies ("Quick Add · M").
          const sizeGroups = groupBySize(available);
          if (sizeGroups.length === 1) {
            this._showConditionStep(wrapper, btn, sizeGroups[0], origText, sizeGroups);
          } else {
            this._showSizeStep(wrapper, btn, sizeGroups, origText);
          }
        } else if (available.length === 1) {
          // Single available variant → add directly
          await this._addVariant(wrapper, btn, available[0], origText);
        } else {
          // Multiple available variants → show size picker
          this._showSizePicker(wrapper, btn, available, origText);
        }
      } catch (err) {
        console.error('[quick-add]', err);
        this._showTempState(wrapper, btn, origText, this.settings.unavailableText, '--error');
      }
    }

    /* ---- Size picker ------------------------------------------------ */
    _showSizePicker(wrapper, triggerBtn, availableVariants, origText) {
      const panel = wrapper.querySelector('.algolia-quick-add__panel');
      panel.innerHTML = '';
      availableVariants.forEach(v => {
        const sizeBtn = document.createElement('button');
        sizeBtn.classList.add('algolia-quick-add__size-btn');
        sizeBtn.setAttribute('type', 'button');
        sizeBtn.textContent = v.option1 || v.title;

        // If already added, mark it
        if (_addedVariants.has(v.id)) {
          sizeBtn.classList.add('algolia-quick-add__size-btn--added');
          sizeBtn.textContent = `${v.option1 || v.title} \u2713`;
        }

        sizeBtn.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          this._addVariant(wrapper, sizeBtn, v, origText, true);
        });
        panel.appendChild(sizeBtn);
      });

      // Hide trigger, show panel
      triggerBtn.style.display = 'none';
      panel.style.display = '';

      // Restore trigger when mouse leaves the card wrapper
      const cardWrapper = wrapper.closest('.card-wrapper') || wrapper.parentElement;
      const restoreHandler = () => {
        // Small delay so the panel doesn't close before a size click registers
        setTimeout(() => {
          if (!wrapper._busy) {
            panel.style.display = 'none';
            triggerBtn.style.display = '';
            triggerBtn.textContent = origText;
            triggerBtn.disabled = false;
            wrapper.classList.remove('algolia-quick-add--open');
          }
        }, 150);
      };
      cardWrapper.addEventListener('mouseleave', restoreHandler, { once: true });

      wrapper._busy = false;
    }

    /* ---- Reset the panel back to the trigger when the mouse leaves --- */
    _armMouseleaveReset(wrapper, triggerBtn, panel, origText) {
      const cardWrapper = wrapper.closest('.card-wrapper') || wrapper.parentElement;
      const restoreHandler = () => {
        // Small delay so the panel doesn't close before a click registers
        setTimeout(() => {
          if (!wrapper._busy) {
            panel.style.display = 'none';
            triggerBtn.style.display = '';
            triggerBtn.textContent = origText;
            triggerBtn.disabled = false;
            wrapper.classList.remove('algolia-quick-add--open');
          }
        }, 150);
      };
      cardWrapper.addEventListener('mouseleave', restoreHandler, { once: true });
    }

    /* ---- Condition-step flow: size step ------------------------------ */
    _showSizeStep(wrapper, triggerBtn, sizeGroups, origText) {
      const panel = wrapper.querySelector('.algolia-quick-add__panel');
      panel.innerHTML = '';

      const maxOptions = this.settings.maxSizeOptions;
      const visibleGroups = maxOptions > 0 ? sizeGroups.slice(0, maxOptions) : sizeGroups;
      const hiddenCount = sizeGroups.length - visibleGroups.length;

      visibleGroups.forEach(group => {
        const sizeBtn = document.createElement('button');
        sizeBtn.classList.add('algolia-quick-add__size-btn');
        sizeBtn.setAttribute('type', 'button');
        sizeBtn.textContent = group.display;
        sizeBtn.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          this._showConditionStep(wrapper, triggerBtn, group, origText, sizeGroups);
        });
        panel.appendChild(sizeBtn);
      });

      if (hiddenCount > 0) {
        const moreLink = document.createElement('a');
        moreLink.classList.add('algolia-quick-add__more-options');
        moreLink.href = `${window.Shopify.routes.root}products/${wrapper._handle}`;
        moreLink.textContent = this.settings.moreOptionsText.replace('{count}', hiddenCount);
        moreLink.addEventListener('click', (e) => e.stopPropagation());
        panel.appendChild(moreLink);
      }

      triggerBtn.style.display = 'none';
      panel.style.display = '';
      this._armMouseleaveReset(wrapper, triggerBtn, panel, origText);
      wrapper._busy = false;
    }

    /* ---- Condition-step flow: condition step -------------------------- */
    _showConditionStep(wrapper, triggerBtn, group, origText, sizeGroups) {
      const panel = wrapper.querySelector('.algolia-quick-add__panel');
      panel.innerHTML = '';

      group.variants.forEach(v => {
        const condBtn = document.createElement('button');
        condBtn.classList.add('algolia-quick-add__size-btn');
        condBtn.setAttribute('type', 'button');
        condBtn.textContent = v.option2 || v.title;

        // If already added, mark it
        if (_addedVariants.has(v.id)) {
          condBtn.classList.add('algolia-quick-add__size-btn--added');
          condBtn.textContent = `${v.option2 || v.title} \u2713`;
        }

        condBtn.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          this._addVariant(wrapper, condBtn, v, origText, true, v.option2 || v.title);
        });
        panel.appendChild(condBtn);
      });

      const backBtn = document.createElement('button');
      backBtn.classList.add('algolia-quick-add__back-btn');
      backBtn.setAttribute('type', 'button');
      backBtn.setAttribute('aria-label', 'Back to sizes');
      if (this.settings.backStyle === 'icon' && wrapper._backIconTemplate) {
        backBtn.appendChild(wrapper._backIconTemplate.content.cloneNode(true));
      } else {
        backBtn.textContent = this.settings.backText;
      }
      backBtn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this._showSizeStep(wrapper, triggerBtn, sizeGroups, origText);
      });
      panel.appendChild(backBtn);

      triggerBtn.style.display = 'none';
      panel.style.display = '';
      this._armMouseleaveReset(wrapper, triggerBtn, panel, origText);
      wrapper._busy = false;
    }

    /* ---- Add a specific variant to cart ------------------------------ */
    async _addVariant(wrapper, btn, variant, origText, isSizeBtn = false, label) {
      const s = this.settings;
      const btnLabel = label || variant.option1 || variant.title;

      // Already added this session?
      if (_addedVariants.has(variant.id)) {
        if (isSizeBtn) {
          // Briefly flash "In Bag"
          btn.textContent = s.inBagText;
          btn.classList.add('algolia-quick-add__size-btn--added');
          setTimeout(() => {
            btn.textContent = `${btnLabel} \u2713`;
          }, 1200);
          return;
        }
        this._showTempState(wrapper, btn, origText, s.inBagText, '--success');
        return;
      }

      wrapper._busy = true;
      wrapper.classList.add('algolia-quick-add--open');
      const prevText = btn.textContent;
      btn.textContent = 'Adding\u2026';
      btn.disabled = true;

      try {
        await SHOPIFY_UTILS.addToShopifyCart({ variantId: variant.id });
        _addedVariants.add(variant.id);
        await SHOPIFY_UTILS.refreshCartIconBubble();

        if (isSizeBtn) {
          btn.textContent = `${btnLabel} \u2713`;
          btn.classList.add('algolia-quick-add__size-btn--added');
          btn.disabled = false;
          wrapper._busy = false;
          openCartDrawer();
          return;
        }

        btn.textContent = s.successText;
        btn.classList.add('algolia-quick-add__trigger--success');
        openCartDrawer();

        setTimeout(() => {
          btn.textContent = origText;
          btn.disabled = false;
          btn.classList.remove('algolia-quick-add__trigger--success');
          wrapper._busy = false;
          wrapper.classList.remove('algolia-quick-add--open');
        }, 1500);

      } catch (err) {
        console.error('[quick-add] Add to cart failed:', err);
        const errMsg = err.message || '';

        // Detect inventory/sold-out errors
        const isSoldOut = /sold out|inventory|not enough|no more/i.test(errMsg);

        if (isSizeBtn) {
          btn.textContent = isSoldOut ? s.soldOutText : 'Error';
          btn.classList.add(isSoldOut ? 'algolia-quick-add__size-btn--soldout' : 'algolia-quick-add__size-btn--error');
          btn.disabled = isSoldOut;
          wrapper._busy = false;
          return;
        }

        if (isSoldOut) {
          this._showTempState(wrapper, btn, origText, s.soldOutText, '--soldout');
        } else {
          btn.textContent = 'Error \u2013 Tap to Retry';
          btn.classList.add('algolia-quick-add__trigger--error');
          btn.disabled = false;
          wrapper._busy = false;
        }
      }
    }

    /* ---- Show a temporary state then reset -------------------------- */
    _showTempState(wrapper, btn, origText, text, modifier) {
      const cls = `algolia-quick-add__trigger${modifier}`;
      btn.textContent = text;
      btn.classList.add(cls);
      setTimeout(() => {
        btn.textContent = origText;
        btn.disabled = false;
        btn.classList.remove(cls);
        wrapper._busy = false;
        wrapper.classList.remove('algolia-quick-add--open');
      }, 2000);
    }
  }

  customElements.define('algolia-quick-add', AlgoliaQuickAdd);

})();
